/**
 * File Helper Utilities
 * Provides reusable functions for file operations including PDF generation
 */

import {
  VideoCallStatusUpdate,
  sessionType,
} from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { getDateFormat, getTimeFormat } from '@vantiva-sa/homesight_humaui/Utilities/DateTime';
import { getUsername } from '@vantiva-sa/homesight_humaui/Utilities/CommonUtilities';
import { EVENT_STATUS, VIDEO_PLAY_STATUS } from '@/constants/device';
import {
  formatWatchDuration,
  formatPercent,
} from '@/Components/ContentActivity/ContentActivityUtils';
import {
  buildReportIcons,
  fetchCircularImageDataUrl,
  getAlertIconKey,
  renderDonutChartDataUrl,
} from './reportGraphics';

/**
 * Generates and exports a PDF report for call activity
 * @param {Object} params - Parameters for PDF generation
 * @param {Function} params.trans - Translation function
 * @param {Object} params.userInfo - Client user information
 * @param {Object} params.currentUser - Current logged-in user information
 * @param {Array} params.formattedList - Formatted list of call activities grouped by date
 * @param {string} params.userTimezone - User's timezone
 * @param {Object} params.loginUserTimeformat - User's time format preferences
 * @param {Array} params.contacts - List of contacts
 * @param {Array} params.userHubsData - List of user hub data
 * @returns {Promise<void>}
 */
export const exportCallActivityToPDF = async ({
  trans,
  userInfo,
  currentUser,
  formattedList,
  userTimezone,
  loginUserTimeformat,
  contacts = [],
  userHubsData = [],
}) => {
  // Lazy load PDF libraries only when export is triggered
  const [{ jsPDF }, autoTable] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Add title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  const reportTitle = trans('CALLACTIVITY') + ' ' + (trans('REPORT') || 'Report');
  doc.text(reportTitle, pageWidth / 2, 15, { align: 'center' });

  // Add client name
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  const clientName = userInfo ? getUsername(userInfo) : 'N/A';
  const clientLabel = trans('CLIENT') || 'Client';
  doc.text(`${clientLabel}: ${clientName}`, pageWidth / 2, 22, { align: 'center' });

  // Add requestor name
  const requestorName = currentUser ? getUsername(currentUser) : 'N/A';
  const requestorLabel = trans('REQUESTOR') || 'Requestor';
  doc.text(`${requestorLabel}: ${requestorName}`, pageWidth / 2, 29, { align: 'center' });

  // Add generation date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const generatedDate = new Date().toLocaleString();
  const generatedLabel = trans('GENERATED') || 'Generated';
  doc.text(`${generatedLabel}: ${generatedDate}`, pageWidth / 2, 36, { align: 'center' });

  // Check if any call has duration to determine if we should show duration column
  let hasDuration = false;
  formattedList.forEach((dayList) => {
    dayList.forEach((item) => {
      // Check both event_status (for schedule events) and call_status__status (for call notifications)
      const isCompleted =
        item.event_status === EVENT_STATUS.COMPLETED ||
        item.call_status__status === VideoCallStatusUpdate.completed;
      if (isCompleted && item.duration) {
        hasDuration = true;
      }
    });
  });

  // Helper function to get participant names
  const getParticipantNames = (invitedContacts) => {
    if (!invitedContacts || !Array.isArray(invitedContacts)) {
      return '';
    }

    const allUsers = [...contacts, ...userHubsData, ...(userInfo ? [userInfo] : [])];

    return invitedContacts
      .map((contact) => {
        const contactUuid = contact.useruuid || contact;
        const userObj = allUsers.find((u) => u.useruuid === contactUuid);
        return userObj ? getUsername(userObj) : contactUuid;
      })
      .join(', ');
  };

  // Helper function to get participant count
  const getParticipantCount = (invitedContacts) => {
    if (!invitedContacts || !Array.isArray(invitedContacts)) {
      return 0;
    }
    return invitedContacts.length;
  };

  // Prepare data for table
  const tableData = [];

  formattedList.forEach((dayList) => {
    dayList.forEach((item, index) => {
      const dateObj = getDateFormat(item?.event_timestamp, userTimezone);
      const timeStr = getTimeFormat(
        item.event_timestamp,
        userTimezone,
        loginUserTimeformat?.is24hoursformat
      );

      // Get call status
      let status = '';
      let duration = '';

      // Check event_status first (for schedule events)
      if (item.event_status) {
        if (item.event_status === EVENT_STATUS.COMPLETED) {
          status = trans('COMPLETED') || 'Completed';
          if (item.duration) {
            const minutes = Math.ceil(item.duration / 60);
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            if (hours > 0) {
              duration = `${hours}h ${remainingMinutes}m`;
            } else {
              duration = `${remainingMinutes}m`;
            }
          }
        } else if (item.event_status === EVENT_STATUS.EXPIRED) {
          status = trans('MISSED') || 'Missed';
        } else {
          // Handle other event statuses generically
          status =
            trans(item.event_status.toUpperCase()) ||
            item.event_status.charAt(0).toUpperCase() + item.event_status.slice(1);
        }
      }
      // Fall back to call_status__status (for call notifications)
      else if (item.call_status__status === VideoCallStatusUpdate.completed) {
        status = trans('COMPLETED') || 'Completed';
        if (item.duration) {
          const minutes = Math.ceil(item.duration / 60);
          const hours = Math.floor(minutes / 60);
          const remainingMinutes = minutes % 60;
          if (hours > 0) {
            duration = `${hours}h ${remainingMinutes}m`;
          } else {
            duration = `${remainingMinutes}m`;
          }
        }
      } else if (item.call_status__status === VideoCallStatusUpdate.missed) {
        status = trans('MISSED') || 'Missed';
      } else if (item.call_status__status === VideoCallStatusUpdate.declined) {
        status = trans('DECLINED') || 'Declined';
      } else if (item.call_status__status) {
        status =
          trans(item.call_status__status.toUpperCase()) ||
          item.call_status__status.charAt(0).toUpperCase() + item.call_status__status.slice(1);
      }

      // Get participants
      const participantCount = getParticipantCount(item.invited_contacts);
      const participantNames = getParticipantNames(item.invited_contacts);

      // Determine if the call is from a scheduled event
      const isScheduledEvent = item?.schedule_eventuuid && (item?.event_name || item?.recurring);

      // Get call type
      let callType = '';
      const scheduledLabel = trans('SCHEDULED') || 'Scheduled';
      const adHocLabel = trans('ADHOC') || 'Ad-hoc';

      if (isScheduledEvent) {
        callType =
          item.event_type?.toLowerCase() === sessionType.zoom
            ? `${scheduledLabel} ${trans('ZOOMCALL')}`
            : `${scheduledLabel} ${trans('VIDEOCALL')}`;
      } else {
        callType =
          item.event_type?.toLowerCase() === sessionType.zoom
            ? `${adHocLabel} ${trans('ZOOMCALL')}`
            : `${adHocLabel} ${trans('VIDEOCALL')}`;
      }

      // Get direction
      let direction = '';
      if (item.incoming !== undefined && participantCount <= 2) {
        direction = item.incoming
          ? trans('INCOMING') || 'Incoming'
          : trans('OUTGOING') || 'Outgoing';
      }

      // Build row data conditionally based on whether duration column should be shown
      const rowData = [
        index === 0 ? dateObj.dayDateformat : '',
        timeStr,
        callType,
        direction,
        participantCount.toString(),
        participantNames || 'N/A',
      ];

      if (hasDuration) {
        rowData.push(duration || '');
      }

      rowData.push(status);

      tableData.push(rowData);
    });
  });

  // Add table
  const tableHeaders = hasDuration
    ? [
        [
          trans('DATE') || 'Date',
          trans('TIME_') || trans('TIME') || 'Time',
          trans('TYPE') || 'Type',
          trans('DIRECTION') || 'Direction',
          trans('PARTICIPANTS') || 'Participants',
          trans('PARTICIPANT_NAMES') || 'Participant Names',
          trans('DURATION') || 'Duration',
          trans('STATUS') || 'Status',
        ],
      ]
    : [
        [
          trans('DATE') || 'Date',
          trans('TIME_') || trans('TIME') || 'Time',
          trans('TYPE') || 'Type',
          trans('DIRECTION') || 'Direction',
          trans('PARTICIPANTS') || 'Participants',
          trans('PARTICIPANT_NAMES') || 'Participant Names',
          trans('STATUS') || 'Status',
        ],
      ];

  const columnStyles = hasDuration
    ? {
        0: { cellWidth: 22, halign: 'left' }, // Date
        1: { cellWidth: 16, halign: 'center' }, // Time
        2: { cellWidth: 26, halign: 'center' }, // Type
        3: { cellWidth: 16, halign: 'center' }, // Direction
        4: { cellWidth: 18, halign: 'center' }, // Participants count
        5: { cellWidth: 42, halign: 'left' }, // Participant Names
        6: { cellWidth: 16, halign: 'center' }, // Duration
        7: { cellWidth: 17, halign: 'center' }, // Status
      }
    : {
        0: { cellWidth: 22, halign: 'left' }, // Date
        1: { cellWidth: 16, halign: 'center' }, // Time
        2: { cellWidth: 30, halign: 'center' }, // Type
        3: { cellWidth: 18, halign: 'center' }, // Direction
        4: { cellWidth: 20, halign: 'center' }, // Participants count
        5: { cellWidth: 58, halign: 'left' }, // Participant Names
        6: { cellWidth: 19, halign: 'center' }, // Status
      };

  autoTable.default(doc, {
    startY: 42,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [0, 50, 82],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
      minCellHeight: 12,
      valign: 'middle',
      halign: 'center',
      overflow: 'visible',
    },
    bodyStyles: {
      fontSize: 7,
    },
    columnStyles: columnStyles,
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 35, left: 5, right: 5 },
    styles: {
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
  });

  // Add footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: 'center',
    });
  }

  // Save the PDF
  const now = new Date();
  const currentUserLocale = currentUser?.locale || 'en-US';

  // Determine date format based on locale (MMDDYYYY for US-based locales, DDMMYYYY for non-US locales)
  const isUSBasedLocale = currentUserLocale.endsWith('-US');
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = isUSBasedLocale ? `${month}${day}${year}` : `${day}${month}${year}`;

  const timeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
  const clientNameForFile = clientName.replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `call-activity-report-${clientNameForFile}-${dateStr}-${timeStr}.pdf`;
  doc.save(fileName);
};

/**
 * Generates and exports a PDF report for content activity (viewing history).
 * Mirrors {@link exportCallActivityToPDF}: header/user block plus a per-date
 * grouped table of viewing sessions and an optional summary block.
 *
 * @param {Object} params
 * @param {Function} params.trans - Translation function
 * @param {Object} params.userInfo - Client user information
 * @param {Object} params.currentUser - Current logged-in user information
 * @param {Array}  params.historyByDate - Grouped viewing history [{ dateKey, dateLabel, items }]
 * @param {Object} [params.summary] - Aggregated summary block (optional top section)
 * @param {string} params.userTimezone - User's timezone
 * @param {Object} params.loginUserTimeformat - User's time format preferences
 * @returns {Promise<void>}
 */
export const exportContentActivityToPDF = async ({
  trans,
  userInfo,
  currentUser,
  historyByDate = [],
  summary,
  userTimezone,
  loginUserTimeformat,
}) => {
  // Lazy load PDF libraries only when export is triggered
  const [{ jsPDF }, autoTable] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();

  // Add title
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  const reportTitle = trans('CONTENTACTIVITY') + ' ' + (trans('REPORT') || 'Report');
  doc.text(reportTitle, pageWidth / 2, 15, { align: 'center' });

  // Add client name
  doc.setFontSize(11);
  doc.setFont(undefined, 'normal');
  const clientName = userInfo ? getUsername(userInfo) : 'N/A';
  const clientLabel = trans('CLIENT') || 'Client';
  doc.text(`${clientLabel}: ${clientName}`, pageWidth / 2, 22, { align: 'center' });

  // Add requestor name
  const requestorName = currentUser ? getUsername(currentUser) : 'N/A';
  const requestorLabel = trans('REQUESTOR') || 'Requestor';
  doc.text(`${requestorLabel}: ${requestorName}`, pageWidth / 2, 29, { align: 'center' });

  // Add generation date
  doc.setFontSize(10);
  doc.setFont(undefined, 'normal');
  const generatedDate = new Date().toLocaleString();
  const generatedLabel = trans('GENERATED') || 'Generated';
  doc.text(`${generatedLabel}: ${generatedDate}`, pageWidth / 2, 36, { align: 'center' });

  let startY = 42;

  const toCellText = (value) => {
    if (value === null || value === undefined) return '';
    const text = String(value);
    return text === '-' || text === '—' ? '' : text;
  };

  // Optional compact summary block
  if (summary && summary.sessionCount > 0) {
    const completionRateText =
      summary.completionRate === null || summary.completionRate === undefined
        ? ''
        : formatPercent(summary.completionRate * 100);
    const summaryRows = [
      [trans('TOTAL_WATCH_TIME') || 'Total Watch Time', formatWatchDuration(summary.totalWatchMs)],
      [trans('VIEWING_SESSIONS') || 'Sessions', `${summary.sessionCount}`],
      [trans('COMPLETION_RATE') || 'Completion Rate', completionRateText],
      [trans('ACTIVE_DAYS') || 'Active Days', `${summary.activeDays}`],
      [
        trans('FAVORITE_CHANNEL') || 'Favorite Channel',
        toCellText(summary.favoriteChannel?.channelName),
      ],
    ];
    autoTable.default(doc, {
      startY,
      body: summaryRows,
      theme: 'plain',
      styles: { fontSize: 9, cellPadding: 2 },
      columnStyles: {
        0: { fontStyle: 'bold', cellWidth: 60 },
        1: { cellWidth: 60 },
      },
      margin: { left: 5, right: 5 },
    });
    startY = doc.lastAutoTable.finalY + 6;
  }

  // Build grouped history table
  const tableData = [];
  historyByDate.forEach((group) => {
    (group.items || []).forEach((item, index) => {
      const timeStr = getTimeFormat(item.ts, userTimezone, loginUserTimeformat?.is24hoursformat);
      const statusText =
        item.status === VIDEO_PLAY_STATUS.COMPLETED
          ? trans('COMPLETED') || 'Completed'
          : item.percentWatched === null || item.percentWatched === undefined
            ? ''
            : trans('WATCHED') || 'Watched';
      tableData.push([
        index === 0 ? toCellText(group.dateLabel || group.dateKey) : '',
        toCellText(timeStr),
        toCellText(item.videoTitle),
        toCellText(item.themeName),
        toCellText(item.channelName),
        toCellText(formatWatchDuration(item.watchMs)),
        toCellText(statusText),
      ]);
    });
  });

  const tableHeaders = [
    [
      trans('DATE') || 'Date',
      trans('TIME_') || trans('TIME') || 'Time',
      trans('TITLE') || 'Title',
      trans('THEME') || 'Theme',
      trans('CHANNEL') || 'Channel',
      trans('DURATION') || 'Duration',
      trans('STATUS') || 'Status',
    ],
  ];

  autoTable.default(doc, {
    startY,
    head: tableHeaders,
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [0, 50, 82],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 8,
      cellPadding: 3,
      minCellHeight: 12,
      valign: 'middle',
      halign: 'center',
      overflow: 'visible',
    },
    bodyStyles: {
      fontSize: 7,
    },
    columnStyles: {
      0: { cellWidth: 26, halign: 'left' }, // Date
      1: { cellWidth: 16, halign: 'center' }, // Time
      2: { cellWidth: 44, halign: 'left' }, // Title
      3: { cellWidth: 30, halign: 'left' }, // Theme
      4: { cellWidth: 30, halign: 'left' }, // Channel
      5: { cellWidth: 18, halign: 'center' }, // Duration
      6: { cellWidth: 22, halign: 'center' }, // Status
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
    margin: { top: 35, left: 5, right: 5 },
    styles: {
      overflow: 'linebreak',
      cellWidth: 'wrap',
    },
  });

  // Add footer
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, doc.internal.pageSize.getHeight() - 10, {
      align: 'center',
    });
  }

  // Save the PDF
  const now = new Date();
  const currentUserLocale = currentUser?.locale || 'en-US';
  const isUSBasedLocale = currentUserLocale.endsWith('-US');
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const dateStr = isUSBasedLocale ? `${month}${day}${year}` : `${day}${month}${year}`;
  const fileTimeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
  const clientNameForFile = clientName.replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `content-activity-report-${clientNameForFile}-${dateStr}-${fileTimeStr}.pdf`;
  doc.save(fileName);
};

/**
 * Report types supported by the dashboard PDF export.
 */
export const DASHBOARD_REPORT_TYPES = {
  SUMMARY: 'summary',
  DETAIL: 'detail',
};

/**
 * Generates and exports a clean, readable PDF report for the Account Dashboard.
 * The `detail` variant is an addendum to `summary`: it retains the full summary
 * structure (top alert cards, recent activity, task-compliance totals) and then
 * appends schedule/weather, a per-task compliance breakdown, and a grouped
 * alert history.
 *
 * @param {Object} params
 * @param {Function} params.trans - Translation function
 * @param {string} params.reportType - One of DASHBOARD_REPORT_TYPES
 * @param {string} params.accountName - Selected account/client display name
 * @param {Object} params.currentUser - Current logged-in (requesting) user
 * @param {Array}  params.topAlerts - Top alert rows [{type,date,time,category,ruleName,location}]
 * @param {Array}  params.alertHistory - Full alert history rows [{type,utc,date,time,category,ruleName}] (detail only)
 * @param {Array}  params.recentActivity - Rows [{iconKey,activity,value,when,calls?}]
 * @param {Object} params.taskCompliance - { stats }
 * @param {Array}  params.schedule - Schedule rows [{title,time,category}] (detail only)
 * @param {Object} params.weather - { text } current weather line (detail only)
 * @returns {Promise<void>}
 */
export const exportDashboardToPDF = async ({
  trans,
  reportType = DASHBOARD_REPORT_TYPES.SUMMARY,
  accountName,
  currentUser,
  topAlerts = [],
  alertHistory = [],
  recentActivity = [],
  taskCompliance = null,
  schedule = [],
  weather = null,
}) => {
  const [{ jsPDF }, autoTable] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const isDetail = reportType === DASHBOARD_REPORT_TYPES.DETAIL;
  const doc = new jsPDF();

  // Rasterize badge icons and resolve caller photos up-front (async), then draw.
  const icons = await buildReportIcons();
  const photoUrls = new Set();
  recentActivity.forEach((row) => {
    (row.calls || []).forEach((c) => {
      if (c.photoUrl) photoUrls.add(c.photoUrl);
    });
  });
  const photoEntries = await Promise.all(
    [...photoUrls].map(async (url) => [url, await fetchCircularImageDataUrl(url)])
  );
  const photoMap = Object.fromEntries(photoEntries);
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const marginX = 14;
  const HEAD_FILL = [0, 50, 82];

  const t = (key, fallback) => {
    const value = trans?.(key);
    return !value || value === key ? fallback : value;
  };

  // ── Title ────────────────────────────────────────────────────────────────
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  const variantLabel = isDetail ? t('DETAIL', 'Detail') : t('SUMMARY', 'Summary');
  const reportTitle = `${t(
    'HOMESIGHT_ACCOUNT_DASHBOARD',
    'HomeSight Account Dashboard'
  )} - ${variantLabel}`;
  doc.text(reportTitle, pageWidth / 2, 18, { align: 'center' });
  doc.setFont(undefined, 'normal');

  // ── Header info block ──────────────────────────────────────────────────────
  doc.setFontSize(10);
  const requestorName = currentUser ? getUsername(currentUser) : 'N/A';
  const generatedDate = new Date().toLocaleString();
  const headerLines = [
    `${t('ACCOUNT', 'Account')}: ${accountName || 'N/A'}`,
    `${t('REQUESTOR', 'Requestor')}: ${requestorName}`,
    `${t('GENERATED', 'Generated')}: ${generatedDate}`,
  ];
  let cursorY = 32;
  headerLines.forEach((line) => {
    doc.text(line, marginX, cursorY);
    cursorY += 6;
  });

  const ensureSpace = (needed) => {
    if (cursorY + needed > pageHeight - 16) {
      doc.addPage();
      cursorY = 18;
    }
  };

  const addSectionTitle = (title) => {
    ensureSpace(14);
    cursorY += 4;
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(title, marginX, cursorY);
    doc.setFont(undefined, 'normal');
    cursorY += 3;
  };

  const addTable = (head, body, columnStyles) => {
    autoTable.default(doc, {
      startY: cursorY,
      head: [head],
      body,
      theme: 'grid',
      headStyles: {
        fillColor: HEAD_FILL,
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 9,
        cellPadding: 3,
      },
      bodyStyles: { fontSize: 8, cellPadding: 2.5 },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      columnStyles: columnStyles || {},
      margin: { left: marginX, right: marginX },
      styles: { overflow: 'linebreak' },
    });
    cursorY = doc.lastAutoTable.finalY + 4;
  };

  const addEmptyLine = (text) => {
    ensureSpace(10);
    doc.setFontSize(9);
    doc.setTextColor(120);
    doc.text(text, marginX, cursorY + 2);
    doc.setTextColor(0);
    cursorY += 8;
  };

  const truncate = (text, max) => {
    const str = String(text ?? '');
    return str.length > max ? `${str.slice(0, max - 1)}...` : str;
  };

  // ── Top Alerts (cards) ──────────────────────────────────────────────────────
  // Cards are laid out left-to-right in reverse-chronological order (newest
  // first) and wrap onto additional rows as needed. Icons match the on-screen
  // alert-category iconography.
  const drawTopAlertCards = (alerts) => {
    const gap = 4;
    const contentW = pageWidth - marginX * 2;
    const minCardW = 56;
    const perRow = Math.max(1, Math.floor((contentW + gap) / (minCardW + gap)));
    const cardW = (contentW - gap * (perRow - 1)) / perRow;
    const cardH = 30;
    const iconSize = 9;

    alerts.forEach((a, i) => {
      const col = i % perRow;
      if (col === 0) ensureSpace(cardH + gap);
      const x = marginX + col * (cardW + gap);
      const y = cursorY;

      doc.setFillColor(248, 250, 252);
      doc.setDrawColor(224, 231, 239);
      doc.roundedRect(x, y, cardW, cardH, 2.5, 2.5, 'FD');

      const iconImg = icons[getAlertIconKey(a.type)];
      if (iconImg) doc.addImage(iconImg, 'PNG', x + 4, y + 4, iconSize, iconSize);

      const textX = x + 4 + iconSize + 3;
      doc.setFontSize(9);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(17, 62, 87);
      doc.text(truncate(a.category, 18), textX, y + 7.5);

      doc.setFontSize(7);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(128, 128, 128);
      doc.text(truncate(`${a.date ?? ''} ${a.time ?? ''}`.trim(), 26), textX, y + 11.5);

      doc.setFontSize(8);
      doc.setTextColor(30, 30, 30);
      const ruleLines = doc.splitTextToSize(String(a.ruleName ?? '-'), cardW - 8).slice(0, 2);
      doc.text(ruleLines, x + 4, y + 18);

      doc.setFontSize(7);
      doc.setTextColor(128, 128, 128);
      doc.text(truncate(a.location ?? '-', 34), x + 4, y + cardH - 3);
      doc.setTextColor(0, 0, 0);

      if (col === perRow - 1 || i === alerts.length - 1) cursorY += cardH + gap;
    });
  };

  // ── Recent Activity (flashy list) ───────────────────────────────────────────
  // Uniform body font for all secondary text in this section.
  const ACTIVITY_FONT = 9;
  const STATUS_PILL_COLORS = {
    completed: [34, 197, 94],
    missed: [239, 68, 68],
    declined: [249, 115, 22],
    failed: [220, 38, 38],
    scheduled: [0, 93, 152],
    ongoing: [17, 62, 87],
  };

  // Draws a rounded "pill" (oval) badge and returns its rendered width.
  const drawPill = (x, y, label, { bg, textColor, iconKey }) => {
    doc.setFontSize(8);
    const padX = 2.5;
    const hasIcon = Boolean(iconKey && icons[iconKey]);
    const iconW = hasIcon ? 3.6 : 0;
    const iconGap = hasIcon ? 1.2 : 0;
    const textW = doc.getTextWidth(label);
    const pillW = padX * 2 + iconW + iconGap + textW;
    const pillH = 5.2;
    doc.setFillColor(bg[0], bg[1], bg[2]);
    doc.roundedRect(x, y, pillW, pillH, 2.6, 2.6, 'F');
    let cx = x + padX;
    if (hasIcon) {
      doc.addImage(icons[iconKey], 'PNG', cx, y + (pillH - iconW) / 2, iconW, iconW);
      cx += iconW + iconGap;
    }
    doc.setTextColor(textColor[0], textColor[1], textColor[2]);
    doc.text(label, cx, y + pillH - 1.6);
    doc.setTextColor(0, 0, 0);
    return pillW;
  };

  const drawCallEntries = (calls) => {
    calls.forEach((c) => {
      const h = 11;
      ensureSpace(h);
      const y = cursorY;
      const size = 8;
      const x0 = marginX + 6;
      const photo = c.photoUrl ? photoMap[c.photoUrl] : null;
      if (photo) {
        doc.addImage(photo, 'PNG', x0, y, size, size);
      } else if (icons.calls) {
        doc.addImage(icons.calls, 'PNG', x0, y, size, size);
      }

      const nameX = x0 + size + 3;
      doc.setFontSize(ACTIVITY_FONT);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(truncate(c.callerName ?? '-', 34), nameX, y + 3.5);
      doc.setFont(undefined, 'normal');

      // Direction + status pills, then duration, beneath the caller name.
      let px = nameX;
      const py = y + 5.2;
      if (c.direction) {
        const label =
          c.direction === 'incoming' ? t('INCOMING', 'Incoming') : t('OUTGOING', 'Outgoing');
        px +=
          drawPill(px, py, label, {
            bg: [226, 232, 240],
            textColor: [51, 65, 85],
            iconKey: c.direction,
          }) + 2;
      }
      if (c.statusLabel) {
        px +=
          drawPill(px, py, c.statusLabel, {
            bg: STATUS_PILL_COLORS[c.statusKey] || [100, 116, 139],
            textColor: [255, 255, 255],
          }) + 2;
      }
      if (c.durationLabel) {
        doc.setFontSize(ACTIVITY_FONT);
        doc.setTextColor(90, 90, 90);
        doc.text(c.durationLabel, px + 0.5, py + 3.7);
        doc.setTextColor(0, 0, 0);
      }

      // Date/time (uniform font size).
      doc.setFontSize(ACTIVITY_FONT);
      doc.setTextColor(110, 110, 110);
      doc.text(truncate(c.when ?? '-', 28), pageWidth - marginX, y + 3.5, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      cursorY += h;
    });
  };

  const drawEngagementEntries = (entries) => {
    entries.forEach((e) => {
      const h = 8.5;
      ensureSpace(h);
      const y = cursorY;
      const size = 7;
      const x0 = marginX + 6;
      if (icons.engagement) doc.addImage(icons.engagement, 'PNG', x0, y, size, size);
      const nameX = x0 + size + 3;
      doc.setFontSize(ACTIVITY_FONT);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(30, 30, 30);
      doc.text(truncate(e.name ?? '-', 44), nameX, y + 4);
      doc.setTextColor(110, 110, 110);
      doc.text(truncate(e.when ?? '-', 28), pageWidth - marginX, y + 4, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      cursorY += h;
    });
  };

  // Draws a small trend indicator (triangle + percentage) and returns its width.
  const drawTrendChip = (rightX, y, percent, dir) => {
    if (dir === null || dir === undefined || percent === null || percent === undefined) return 0;
    const label = `${Math.abs(percent)}%`;
    doc.setFontSize(8);
    const textW = doc.getTextWidth(label);
    const triW = 2.4;
    const gap = 1;
    const totalW = triW + gap + textW;
    const x = rightX - totalW;
    const cy = y - 1.6;
    let color = [120, 120, 120];
    if (dir === 'up') color = [34, 197, 94];
    else if (dir === 'down') color = [239, 68, 68];
    doc.setFillColor(color[0], color[1], color[2]);
    if (dir === 'up') {
      doc.triangle(x, cy + 1.2, x + triW, cy + 1.2, x + triW / 2, cy - 1.2, 'F');
    } else if (dir === 'down') {
      doc.triangle(x, cy - 1.2, x + triW, cy - 1.2, x + triW / 2, cy + 1.2, 'F');
    } else {
      doc.rect(x, cy - 0.3, triW, 0.6, 'F');
    }
    doc.setTextColor(color[0], color[1], color[2]);
    doc.text(label, x + triW + gap, y);
    doc.setTextColor(0, 0, 0);
    return totalW;
  };

  const drawReadingRows = (readings) => {
    readings.forEach((r) => {
      const h = 8.5;
      ensureSpace(h);
      const y = cursorY;
      const x0 = marginX + 6;
      const hasTrend = r.trendDir !== null && r.trendDir !== undefined;
      const whenX = pageWidth - marginX;
      const trendRightX = whenX - 24;

      doc.setFontSize(ACTIVITY_FONT);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(30, 30, 30);
      doc.text(truncate(r.label ?? '-', 20), x0, y + 4);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(90, 90, 90);
      doc.text(truncate(r.value ?? '-', 22), x0 + 38, y + 4);

      if (hasTrend) drawTrendChip(trendRightX, y + 4, r.trendPercent, r.trendDir);

      doc.setTextColor(110, 110, 110);
      doc.text(truncate(r.when ?? '-', 20), whenX, y + 4, { align: 'right' });
      doc.setTextColor(0, 0, 0);
      cursorY += h;
    });
  };

  const drawRecentActivity = (rows) => {
    rows.forEach((row) => {
      const hasCalls = Array.isArray(row.calls) && row.calls.length > 0;
      const hasEngagement = Array.isArray(row.engagement) && row.engagement.length > 0;
      const hasReadings = Array.isArray(row.readings) && row.readings.length > 0;
      const hasSubList = hasCalls || hasEngagement || hasReadings;

      const rowH = 11;
      ensureSpace(rowH);
      const y = cursorY;
      const bSize = 8;
      const badge = icons[row.iconKey];
      if (badge) doc.addImage(badge, 'PNG', marginX, y, bSize, bSize);

      const textX = marginX + bSize + 4;
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(17, 62, 87);
      doc.text(truncate(row.activity ?? '-', 40), textX, y + 4);
      doc.setFont(undefined, 'normal');

      if (!hasSubList) {
        doc.setFontSize(ACTIVITY_FONT);
        doc.setTextColor(90, 90, 90);
        doc.text(truncate(row.value ?? '-', 48), textX, y + 8.5);
        if (row.trendDir !== null && row.trendDir !== undefined) {
          drawTrendChip(pageWidth - marginX - 24, y + 8.5, row.trendPercent, row.trendDir);
        }
        doc.setTextColor(110, 110, 110);
        doc.text(truncate(row.when ?? '-', 28), pageWidth - marginX, y + 4, { align: 'right' });
        doc.setTextColor(0, 0, 0);
      }

      cursorY += hasSubList ? 8 : rowH;
      doc.setDrawColor(235, 235, 235);
      doc.line(marginX, cursorY - 2, pageWidth - marginX, cursorY - 2);
      if (hasCalls) drawCallEntries(row.calls);
      else if (hasEngagement) drawEngagementEntries(row.engagement);
      else if (hasReadings) drawReadingRows(row.readings);
      cursorY += 2;
    });
  };

  // Groups alert history by category, then rule name, retaining occurrence
  // counts and the first/last time each rule fired (keeps the report compact).
  const groupAlertHistory = (historyRows) => {
    const map = new Map();
    historyRows.forEach((r) => {
      const category = r.category ?? '-';
      const ruleName = r.ruleName ?? '-';
      const key = `${category}||${ruleName}`;
      const time = r.utc ? new Date(r.utc).getTime() : NaN;
      let group = map.get(key);
      if (!group) {
        group = {
          category,
          ruleName,
          count: 0,
          firstT: Infinity,
          lastT: -Infinity,
          firstLabel: '-',
          lastLabel: '-',
        };
        map.set(key, group);
      }
      group.count += 1;
      const label = `${r.date ?? ''} ${r.time ?? ''}`.trim() || '-';
      if (!Number.isNaN(time)) {
        if (time < group.firstT) {
          group.firstT = time;
          group.firstLabel = label;
        }
        if (time > group.lastT) {
          group.lastT = time;
          group.lastLabel = label;
        }
      }
    });
    return [...map.values()].sort(
      (a, b) => a.category.localeCompare(b.category) || b.count - a.count
    );
  };

  // ── Task Compliance (donut + calendar) ──────────────────────────────────────
  const MONTH_NAMES = Array.from({ length: 12 }, (_, i) =>
    new Date(Date.UTC(2000, i, 1)).toLocaleDateString(currentUser?.locale || 'en-US', {
      month: 'long',
    })
  );

  const drawTaskCompliance = (tc) => {
    const stats = tc?.stats || {};
    const completed = Number(stats.completed) || 0;
    const missed = Number(stats.missed) || 0;
    const total = Number(stats.total) || 0;
    const rate = total > 0 ? Math.round((completed / total) * 100) : 0;

    const blockH = 72;
    ensureSpace(blockH);
    const top = cursorY;

    // Left column: donut chart (completed vs missed) with centered rate.
    const donutSize = 40;
    const donutX = marginX;
    const donutY = top;
    const donut = renderDonutChartDataUrl(
      [
        { value: completed, color: '#22C55E' },
        { value: missed, color: '#EF4444' },
      ],
      280
    );
    if (donut) doc.addImage(donut, 'PNG', donutX, donutY, donutSize, donutSize);
    doc.setFontSize(15);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(17, 62, 87);
    doc.text(`${rate}%`, donutX + donutSize / 2, donutY + donutSize / 2 + 1, { align: 'center' });
    doc.setFontSize(7);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(120, 120, 120);
    doc.text(t('RATE', 'Rate'), donutX + donutSize / 2, donutY + donutSize / 2 + 5, {
      align: 'center',
    });
    doc.setTextColor(0, 0, 0);

    // Period label + legend below the donut.
    let ly = donutY + donutSize + 5;
    doc.setFontSize(8);
    if (tc?.periodLabel) {
      doc.setTextColor(90, 90, 90);
      doc.text(truncate(tc.periodLabel, 24), donutX, ly);
      ly += 5;
    }
    [
      { color: [34, 197, 94], label: `${t('COMPLETED', 'Completed')}: ${completed}` },
      { color: [239, 68, 68], label: `${t('MISSED', 'Missed')}: ${missed}` },
    ].forEach((item) => {
      doc.setFillColor(item.color[0], item.color[1], item.color[2]);
      doc.circle(donutX + 1.5, ly - 1.4, 1.4, 'F');
      doc.setTextColor(60, 60, 60);
      doc.text(item.label, donutX + 5, ly);
      ly += 5;
    });
    doc.setTextColor(0, 0, 0);

    // Right column: month calendar colored by compliance status.
    const calX = marginX + 56;
    const dailyTasks = Array.isArray(tc?.dailyTasks) ? tc.dailyTasks : [];
    const tasksByDate = {};
    dailyTasks.forEach((d) => {
      if (d?.date) tasksByDate[d.date] = d;
    });

    const monthISO = tc?.monthISO || new Date().toISOString().slice(0, 7);
    const [yearNum, monthNum] = monthISO.split('-').map(Number);
    const monthIndex = (monthNum || 1) - 1;
    const firstDay = new Date(yearNum, monthIndex, 1);
    const leading = (firstDay.getDay() + 6) % 7; // Monday-first offset
    const daysInMonth = new Date(yearNum, monthIndex + 1, 0).getDate();

    doc.setFontSize(10);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(17, 62, 87);
    doc.text(`${MONTH_NAMES[monthIndex] || ''} ${yearNum}`, calX, top + 3);
    doc.setFont(undefined, 'normal');

    const cols = 7;
    const cell = 10;
    let gridY = top + 6;
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 140);
    const weekdayLabels = Array.from({ length: 7 }, (_, i) =>
      new Date(Date.UTC(2023, 0, 2 + i)).toLocaleDateString(currentUser?.locale || 'en-US', {
        weekday: 'short',
      })
    );
    weekdayLabels.forEach((d, i) => {
      doc.text(d, calX + i * cell + cell / 2, gridY + 3, { align: 'center' });
    });
    gridY += 5;

    const STATUS_FILL = {
      completed: [34, 197, 94],
      expired: [239, 68, 68],
      scheduled: [0, 93, 152],
    };
    let dayNum = 1;
    for (let row = 0; row < 6; row++) {
      for (let col = 0; col < cols; col++) {
        const cellIndex = row * cols + col;
        if (cellIndex < leading || dayNum > daysInMonth) continue;
        const cx = calX + col * cell;
        const cy = gridY + row * cell;
        const dateStr = `${yearNum}-${String(monthNum).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
        const dayData = tasksByDate[dateStr];
        let status = '';
        if (dayData?.tasks?.length) {
          const anyMissed = dayData.tasks.some((task) => task.status === 'expired');
          const allCompleted = dayData.tasks.every((task) => task.status === 'completed');
          const anyPending = dayData.tasks.some(
            (task) => task.status === 'ongoing' || task.status === 'scheduled'
          );
          if (anyMissed) status = 'expired';
          else if (allCompleted) status = 'completed';
          else if (anyPending) status = 'scheduled';
        }
        const pad = 0.6;
        if (status && STATUS_FILL[status]) {
          const f = STATUS_FILL[status];
          doc.setFillColor(f[0], f[1], f[2]);
          doc.roundedRect(cx + pad, cy + pad, cell - 2 * pad, cell - 2 * pad, 1, 1, 'F');
          doc.setTextColor(255, 255, 255);
        } else {
          doc.setDrawColor(225, 225, 225);
          doc.roundedRect(cx + pad, cy + pad, cell - 2 * pad, cell - 2 * pad, 1, 1, 'S');
          doc.setTextColor(90, 90, 90);
        }
        doc.setFontSize(6.5);
        doc.text(String(dayNum), cx + cell / 2, cy + cell / 2 + 1.4, { align: 'center' });
        doc.setTextColor(0, 0, 0);
        dayNum++;
      }
    }
    cursorY = top + blockH;
  };

  // ── Top Alerts (summary) ────────────────────────────────────────────────────
  addSectionTitle(t('TOP_ALERTS', 'Top Alerts'));
  if (topAlerts.length > 0) {
    drawTopAlertCards(topAlerts);
  } else {
    addEmptyLine(t('NO_ALERTS_TRIGGERED', 'No alerts triggered'));
  }

  // ── Recent Activity (summary) ────────────────────────────────────────────────
  addSectionTitle(t('RECENT_ACTIVITY', 'Recent Activity'));
  if (recentActivity.length > 0) {
    drawRecentActivity(recentActivity);
  } else {
    addEmptyLine(t('NO_RECENT_ACTIVITY', 'No recent activity'));
  }

  // ── Task Compliance (summary: donut + calendar) ────────────────────────────
  addSectionTitle(t('TASK_COMPLIANCE', 'Task Compliance'));
  if (taskCompliance?.stats && Number(taskCompliance.stats.total) > 0) {
    drawTaskCompliance(taskCompliance);
  } else {
    addEmptyLine(t('NODATAAVAIALBLE', 'No data available'));
  }

  // ── Detail addendum (retains the summary above, appends the following) ───────
  if (isDetail) {
    // Schedule & Weather
    addSectionTitle(t('SCHEDULE_AND_WEATHER', 'Schedule & Weather'));
    if (weather?.text) {
      addEmptyLine(`${t('WEATHER', 'Weather')}: ${weather.text}`);
    }
    if (schedule.length > 0) {
      addTable(
        [t('SCHEDULE', 'Schedule'), t('TIME_', 'Time'), t('CATEGORY', 'Category')],
        schedule.map((e) => [e.title ?? '-', e.time ?? '-', e.category ?? '-']),
        { 0: { cellWidth: 70 } }
      );
    } else if (!weather?.text) {
      addEmptyLine(t('NO_EVENTS_SCHEDULED', 'No events scheduled'));
    }

    // Per-task compliance breakdown
    const byName = taskCompliance?.stats?.taskComplianceByName || {};
    const nameRows = Object.entries(byName).map(([name, v]) => [
      name,
      String(v.total ?? 0),
      String(v.completed ?? 0),
      String(v.missed ?? 0),
      `${Math.round(v.rate ?? 0)}%`,
    ]);
    if (nameRows.length > 0) {
      addSectionTitle(t('TASK_BREAKDOWN', 'Task Breakdown'));
      addTable(
        [
          t('NAME', 'Name'),
          t('TOTAL', 'Total'),
          t('COMPLETED', 'Completed'),
          t('MISSED', 'Missed'),
          t('RATE', 'Rate'),
        ],
        nameRows,
        { 0: { cellWidth: 70 } }
      );
    }

    // Grouped Alert History
    addSectionTitle(t('ALERT_HISTORY', 'Alert History'));
    const groups = groupAlertHistory(alertHistory);
    if (groups.length > 0) {
      addTable(
        [
          t('CATEGORY', 'Category'),
          t('RULE_NAME', 'Rule name'),
          t('OCCURRENCES', 'Occurrences'),
          t('FIRST_SEEN', 'First seen'),
          t('LAST_SEEN', 'Last seen'),
        ],
        groups.map((g) => [g.category, g.ruleName, String(g.count), g.firstLabel, g.lastLabel]),
        { 0: { cellWidth: 26 }, 1: { cellWidth: 58 }, 2: { cellWidth: 22, halign: 'center' } }
      );
    } else {
      addEmptyLine(t('NO_ALERTS_TRIGGERED', 'No alerts triggered'));
    }
  }

  // ── Footer ───────────────────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const now = new Date();
  const currentUserLocale = currentUser?.locale || 'en-US';
  const isUSBasedLocale = currentUserLocale.endsWith('-US');
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const fileDateStr = isUSBasedLocale ? `${month}${day}${year}` : `${day}${month}${year}`;
  const fileTimeStr = now.toTimeString().split(' ')[0].replace(/:/g, '');
  const accountForFile = String(accountName || 'account').replace(/[^a-zA-Z0-9]/g, '_');
  const fileName = `dashboard-${reportType}-report-${accountForFile}-${fileDateStr}-${fileTimeStr}.pdf`;
  doc.save(fileName);
};
